import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { Observable, lastValueFrom, map, take } from 'rxjs';
import { Member } from '../models/member.model';
import { MembershipStatus } from '../models/membership.status';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class MemberService {
  http = inject(HttpClient);
  utilsService = inject(UtilsService);
  constructor(private afs: AngularFirestore) {}

  getMembersByGroup(
    groupId: string,
    limit?: number,
    lastRecord?: Member
  ): Observable<Member[]> {
    // console.log('MembershipService :: getMembersByGroup: Called');

    const membersCollection: AngularFirestoreCollection<Member> =
      this.afs.collection<Member>('memberships', (ref) => {
        return ref
          .where('groupId', '==', groupId)
          .where('status', '==', MembershipStatus.active)
          .limit(limit ?? 10)
          .orderBy('modified', 'desc');
      });

    // console.log(
    //   `MembershipService :: getMembersByGroup: ${JSON.stringify(
    //     membersCollection
    //   )}`
    // );

    // if (lastRecord != null) {
    //   ref = ref.startAfter([
    //     Timestamp.fromMillisecondsSinceEpoch(
    //         lastRecord.modified!.millisecondsSinceEpoch)
    //   ]);
    // }

    return membersCollection.get().pipe(
      take(1),
      map((changes) => {
        return changes.docs.map((a) => {
          var data = a.data() as Member;
          data.id = a.id;
          return data;
        });
      })
    );
  }

  async searchMembers(
    searchTerm: string,
    groupId: string,
    filterFields?: Object
  ): Promise<any> {
    // console.log(
    //   `MembershipService :: searchMembers: Search '${searchTerm}' in group '${groupId}'`
    // );
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'searchMembersByFilters');
      const result = await callable({
        searchTerm: searchTerm,
        groupId: groupId,
        filterFields: filterFields,
      });
      // console.log(`Response: ${JSON.stringify(result)}`);
      return result.data;
    } catch (error) {
      // console.log(`MembershipService :: searchMembers: Error: ${error}`);
      return Promise.reject('Unable to search members');
    }
  }

  create(member: Member): Promise<void> {
    // console.log(`MemberService :: create: ${JSON.stringify(member, null, 2)}`);
    try {
      return this.afs
        .collection('memberships')
        .doc(`${member.groupId}_${member.memberId}`)
        .set(member);
    } catch (error) {
      // console.log(`MemberService :: create: Error: ${error}`);
      const message = this.utilsService.handleError(error);
      throw error;
    }
  }

  update(id: string, member: Member): Promise<void> {
    // console.log(`MemberService :: update: ${JSON.stringify(member, null, 2)}`);
    try {
      return this.afs.collection('memberships').doc(id).update(member);
    } catch (error) {
      // console.log(`MemberService :: update: Error: ${error}`);
      return Promise.reject(error);
    }
  }

  async delete(id: string): Promise<void> {
    return this.afs.collection('memberships').doc(id).delete();
  }

  getMembershipById(groupId: string, uid: string): Promise<Member | null> {
    const snapshot = this.afs
      .collection<Member>('memberships', (ref) =>
        ref
          .where('groupId', '==', groupId)
          .where('memberId', '==', uid)
          .limit(1)
      )
      .get();

    return lastValueFrom(
      snapshot.pipe(
        take(1),
        map((memberships) => {
          if (memberships.empty) return null;
          let member: Member = memberships.docs[0]?.data() as Member;
          member.id = memberships.docs[0].id;
          return member;
        })
      )
    );
  }

  // async requestMembership(data: Member): Promise<any> {
  //   return this.afs
  //     .collection('memberships')
  //     .doc(`${data.groupId}_${data.memberId}`)
  //     .set(data);
  // }

  async getMemberStats(groupId: string): Promise<any> {
    try {
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'memberCount');
      const result = await callable({
        groupId,
      });
      // console.log(`Response: ${JSON.stringify(result)}`);
      return result.data;
    } catch (error) {
      // console.log(`MemberService :: getMemberStats: Error: ${error}`);
      return Promise.reject('Unable to fetch Member Statistics');
    }
  }
}
